import { createContext, ReactNode } from 'react';

import { useCreateTicketState } from '@store/hookstate';

import { CreateTicket, Loading, Sidebar } from '@components';
import { Line } from '@components/SearchBars/SearchBarTemp/SearchBarTemp.styles';
import { InviteUserRole } from '@models/Invite.model';
import { getProfile } from '@services/queries/profile';
import { getSettings } from '@services/queries/settings';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from '@beam-app/types';
import { trackUserLoggedIn } from '@services/mixpanel/trackers/UserSessionMetrics';
import { useAppDispatch } from '@store/redux/store';
import { trackUserProfile } from '@services/mixpanel/trackers/UserProfile';
import { toggleOff } from '@store/redux/features/mixpanel';
import { selectViewport } from '@store/redux/features/mobile/mobileSelectors';
import {
  Container,
  Content,
  SearchBarContainer,
  SidebarContainer,
  TicketContainer,
} from './navigation.styles';

interface INavigation {
  children: ReactNode;
}

export interface INavigationContext {
  role: InviteUserRole;
  studioId: string;
  userName: string;
  userEmail: string;
  updatesMtViewTime: number;
  userPicture: string;
  userId: string;
  isSuperAdmin: boolean;
  studioName: string;
}

export const NavigationContext = createContext<INavigationContext>({
  role: InviteUserRole.MEMBER,
  studioId: '',
  userName: '',
  userEmail: '',
  updatesMtViewTime: 0,
  userPicture: '',
  userId: '',
  isSuperAdmin: false,
  studioName: '',
});

const Navigation = ({ children }: INavigation) => {
  const ticketState = useCreateTicketState();

  const shouldRegisterIdentity = useSelector(
    (state: RootState) => !!state.mixpanelStore['User Logged In'],
  );
  const dispatch = useAppDispatch();

  const viewport = useSelector(selectViewport());
  const { data, isLoading, isError } = useQuery('profile', getProfile, {
    onSuccess(_data) {
      const studioName = _data.userGroup.studio.name;
      if (shouldRegisterIdentity) {
        // This is only set on login
        trackUserLoggedIn(studioName, _data.id, _data.email);
        trackUserProfile(_data.name, _data.email);
        dispatch(toggleOff('User Logged In'));
      }
    },
  });
  const settingsQuery = useQuery('settings', getSettings);

  if (isLoading || !data) {
    return <Loading />;
  }

  if (isError) {
    // @ts-expect-error This is for firefox to make it clear its cache https://developer.mozilla.org/en-US/docs/Web/API/Location/reload
    window.location.reload(true);
    return <div>Something went wrong</div>;
  }

  const updatesMtViewTime = settingsQuery.data?.find(
    item => item.key === 'MAT_VIEW_UPDATES_DELAY_SECONDS',
  )?.value;

  return (
    <NavigationContext.Provider
      value={{
        role: data.role as InviteUserRole,
        studioId: data.userGroup.studioId,
        updatesMtViewTime: updatesMtViewTime ? Number(updatesMtViewTime) : 0,
        userEmail: data.email,
        userName: data.name,
        userPicture: data.avatar,
        userId: data.id,
        isSuperAdmin: data.isSuperAdmin,
        studioName: data.userGroup.studio.name,
      }}
    >
      <Container data-viewport={viewport}>
        <SearchBarContainer>
          <Line />
        </SearchBarContainer>

        <SidebarContainer>
          <Sidebar />
        </SidebarContainer>
        <Content>{children}</Content>

        {ticketState.open.get() && (
          <TicketContainer>
            <CreateTicket />
          </TicketContainer>
        )}
      </Container>
    </NavigationContext.Provider>
  );
};
export { Navigation };
